const STORAGE_KEY = "form_submission_cache";

interface CachedSubmission {
  payload: any;
  timestamp: number;
  sessionId: string;
  formId: string | number;
  pageNumber: number;
  synced: boolean;
}

type CacheStore = Record<string, CachedSubmission>;

function getStore(): CacheStore {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveStore(store: CacheStore): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // localStorage full or unavailable — silently fail
  }
}

export function cacheSubmission(
  key: string,
  payload: any,
  meta: { sessionId: string; formId: string | number; pageNumber: number },
): void {
  if (typeof window === "undefined") return;
  const store = getStore();
  store[key] = {
    payload,
    timestamp: Date.now(),
    sessionId: meta.sessionId,
    formId: meta.formId,
    pageNumber: meta.pageNumber,
    synced: false,
  };
  saveStore(store);
}

export function getCachedSubmission(key: string): CachedSubmission | null {
  if (typeof window === "undefined") return null;
  const store = getStore();
  return store[key] ?? null;
}

export function getCachedSubmissions(sessionId?: string): CachedSubmission[] {
  if (typeof window === "undefined") return [];
  const store = getStore();
  const entries = Object.values(store);
  if (sessionId) {
    return entries.filter((e) => e.sessionId === sessionId);
  }
  return entries;
}

export function markAsSynced(key: string): void {
  if (typeof window === "undefined") return;
  const store = getStore();
  if (store[key]) {
    store[key].synced = true;
    saveStore(store);
  }
}

export function removeCachedSubmission(key: string): void {
  if (typeof window === "undefined") return;
  const store = getStore();
  delete store[key];
  saveStore(store);
}

export function clearSessionCache(sessionId: string): void {
  if (typeof window === "undefined") return;
  const store = getStore();
  const newStore: CacheStore = {};
  for (const [key, entry] of Object.entries(store)) {
    if (entry.sessionId !== sessionId) {
      newStore[key] = entry;
    }
  }
  saveStore(newStore);
}

export function getUnsyncedSubmissions(): CachedSubmission[] {
  if (typeof window === "undefined") return [];
  const store = getStore();
  return Object.values(store).filter((e) => !e.synced);
}
